import { z } from "zod";
import { campaignKept, SiteCampaign, sourceOfHost, taggedSource } from "./site.js";

/**
 * Where a person came from, kept on the person (2026-09-21, the owner: *save the source on signup too*).
 *
 * Joogoos has counted its customers' websites since [ADR 0035](../../../docs/adr/0035-a-visit-is-counted-and-nobody-is-followed.md)
 * and has never counted its own sign-ups. Somebody arrives from an Instagram post, opens the app, signs up, and the
 * one fact worth knowing about that — *what brought them* — was thrown away by the next page load. It is the same
 * asymmetry the ADR 0035 amendment is built on: a screen can be built whenever somebody wants it, and an arrival
 * that was not written down is gone.
 *
 * It is the same reading as a customer's website (`sourceOfHost`), so "Instagram" means one thing everywhere, and it
 * keeps the same three things a visit keeps: the name, the host it was read from, and the tags on the link. No
 * address, no user agent, no path, and nothing that outlives the sign-up — this is one row on one person's own
 * account, written once.
 */

/**
 * What the browser hands back: the host of the first page it ever loaded here, and the tags that were on the link.
 *
 * A host and not a referrer, because the browser has the whole referrer and we want none of it (ADR 0035): a path
 * carries somebody's search terms, and a query string carries worse. `from` may also be a host a link passed along
 * in `?from=`, which is how a hop through joogoos.com would keep the name of what sent them there.
 */
export const Arrival = z.object({
  from: z.string().max(128).optional(),
  campaign: SiteCampaign.optional(),
});
export type Arrival = z.infer<typeof Arrival>;

/** Our own app. Somebody moving between its pages has not arrived from anywhere, so this is read as nothing. */
const OUR_APP = /^(app|dev)\.joogoos\.com$|^localhost$/;

/** Our own marketing site, which did send them — and is the honest answer when nothing says what sent it. */
const OUR_SITE = /^joogoos\.com$/;

/** A host, as it will be stored: lowercase, no `www.`, no port, and nothing that is not a host at all. */
export function arrivalHost(said: string | null | undefined): string | null {
  const host = (said ?? "").trim().toLowerCase().replace(/^[a-z]+:\/\//, "").replace(/[/?#].*$/, "").replace(/:\d+$/, "").replace(/^www\./, "");
  if (!host || host.length > 128) return null;
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/.test(host) ? host : null;
}

/** What is written against a person, or null for all three when the browser had nothing to say. */
export interface ArrivalRead {
  /** "Instagram", "Google", "joogoos.com", the customer's own tag — or null when nothing said anything. */
  source: string | null;
  host: string | null;
  campaign: Record<string, string> | null;
}

/**
 * Read an arrival: the name, and the raw material it was read from.
 *
 * Our own app is dropped, because a reload is not an arrival. Our own site is kept under its own name: *came through
 * joogoos.com* and *came straight to the app* are different facts, and the first is as far as the referrer can take
 * us — a person who found the site on Instagram and then clicked through reads as the site unless the link they
 * followed carried a tag. That is the known limit of this, and the reason tagging our own links matters.
 */
export function readArrival(a: Arrival): ArrivalRead {
  const campaign = campaignKept(a.campaign);
  const host = arrivalHost(a.from);
  if (!host || OUR_APP.test(host)) return { source: taggedSource(a.campaign), host: null, campaign };
  if (OUR_SITE.test(host)) return { source: taggedSource(a.campaign) ?? "joogoos.com", host, campaign };
  return { source: sourceOfHost(host, a.campaign), host, campaign };
}

/** Whether there is anything worth a request at all. An empty arrival is not sent and not stored. */
export const worthSending = (a: Arrival): boolean => !!readArrival(a).source;
